import { objectKeys } from './object';

export function isString(x: unknown): x is string {
  return typeof x === 'string';
}

export function isNumber(x: unknown): x is number {
  return typeof x === 'number';
}

export function isFile(x: unknown): x is File {
  return x instanceof File;
}

export function isPrice(x: unknown): x is number {
  if (!isNumber(x) || Number.isNaN(x) || x < 0 || x > 999999999999) {
    return false;
  }

  const s = String(x);
  const parts = s.split('.');
  const signsAfterPoint = (parts[1] || '').length;

  if (signsAfterPoint > 2) {
    return false;
  }

  return true;
}

export function isInteger(x: unknown): x is number {
  return isNumberStrict(x) && Number.isInteger(x);
}

export function isBoolean(x: unknown): x is boolean {
  return x === true || x === false;
}

export function isIntegerPositive(x: unknown): x is number {
  return isInteger(x) && x > 0;
}

export function isNumberFinite(x: unknown): x is number {
  return typeof x === 'number' && Number.isFinite(x);
}

export function isNumberStrict(x: unknown): x is number {
  return typeof x === 'number' && !Number.isNaN(x);
}

/** Returns true whether the specified string is a part of enum */
export function isEnum<T>(str: unknown, enumType: T): str is T[keyof T] {
  if (!isObject(enumType)) {
    return false;
  }

  const keys = objectKeys(enumType);

  for (const key of keys) {
    if (String(enumType[key]) === String(str)) {
      return true;
    }
  }

  return false;
}

export function isObject(value: unknown): value is Record<string | symbol, unknown> {
  return typeof value === 'object' && !Array.isArray(value) && value !== null && !isFile(value);
}

export function isJson(value: unknown) {
  if (typeof value !== 'string') return false;
  try {
    const result = JSON.parse(value);
    const type = Object.prototype.toString.call(result);
    return type === '[object Object]' || type === '[object Array]';
  } catch (e) {
    return false;
  }
}

export function exhaustiveCheck(x: never): never {
  throw new Error(`Exhaustive check, unexpected value: ${String(x)}`);
}

export function isArrayOfPrimitives(arr: unknown[]): arr is (string | number | null | undefined)[] {
  return arr.length > 0 && !isObject(arr[0]);
}
